"use client";

/**
 * AdminWizard — 단계별 콘텐츠를 "마법사(steps)" 또는 "탭(tabs)"으로 보여주는 컴포넌트
 *
 * 등록(add)처럼 순서대로 입력해야 하는 흐름은 variant="steps"로 — AdminSteps 진행 표시 +
 * 이전/다음 버튼과 함께 현재 단계의 content 만 보여준다. 수정(update)처럼 이미 값이 다 있어서
 * 아무 섹션이나 바로 이동해서 고치는 흐름은 variant="tabs"로 — AdminTabs 로 자유롭게 전환한다.
 *
 * 섹션(제목+내용)을 items 배열 하나로만 정의하면 되고, variant 에 따라 AdminSteps/AdminTabs 로
 * 자동으로 갈라 렌더링해준다 — steps 용 배열과 tabs 용 배열을 따로 만들 필요가 없다.
 * AdminSteps 처럼 완전한 제어 컴포넌트다 (current/onChange 를 직접 관리).
 *
 * @example
 * const [step, setStep] = useState(0);
 * <AdminWizard
 *   variant={mode === "add" ? "steps" : "tabs"}
 *   current={step}
 *   onChange={setStep}
 *   items={[
 *     { key: "basic", title: "기본 정보", content: <KioskBasicInfo form={form} /> },
 *     { key: "camera", title: "카메라", content: <KioskCamera form={form} /> },
 *     { key: "confirm", title: "확인", content: <KioskConfirm form={form} /> },
 *   ]}
 * />
 */
import type { ReactNode } from "react";

import { Button } from "../ui/button";
import { AdminTabs } from "../tabs/admin-tabs";
import { AdminSteps, type AdminStepsSize } from "./admin-steps";

export type AdminWizardVariant = "steps" | "tabs";

export interface AdminWizardItem {
  /** AdminTabs 로 렌더링될 때 사용하는 고유 key (tabs value 로도 쓰인다) */
  key: string;
  title: ReactNode;
  /** variant="steps" 일 때만 표시되는 단계 설명 */
  description?: ReactNode;
  /** 이 단계/탭에서 보여줄 콘텐츠 */
  content: ReactNode;
  disabled?: boolean;
}

export interface AdminWizardProps {
  items: AdminWizardItem[];
  /** "steps"(기본): 이전/다음 버튼이 있는 순차 마법사. "tabs": 자유 이동 탭 */
  variant?: AdminWizardVariant;
  /** 현재 단계/탭 인덱스 */
  current: number;
  onChange: (index: number) => void;
  /** variant="steps" 일 때 이전 버튼 라벨 (기본 "이전") */
  prevLabel?: ReactNode;
  /** variant="steps" 일 때 다음 버튼 라벨 (기본 "다음") */
  nextLabel?: ReactNode;
  /** AdminSteps 크기 (variant="steps" 일 때만 적용, 기본 "md") */
  size?: AdminStepsSize;
  className?: string;
  /** 콘텐츠 영역(각 단계/탭의 content)에 적용할 클래스 */
  contentClassName?: string;
  /**
   * variant="steps" 일 때 기본 제공되는 이전/다음 버튼(하단 네비게이션)을 숨긴다.
   * 모달 footer 등 다른 위치에 직접 이전/다음 버튼을 두고 싶을 때 사용한다 (기본 false=보임).
   * 이 경우 이전/다음 버튼은 그냥 onChange(current - 1) / onChange(current + 1) 을 호출하면 된다.
   */
  hideNav?: boolean;
}

export function AdminWizard({
  items,
  variant = "steps",
  current,
  onChange,
  prevLabel = "이전",
  nextLabel = "다음",
  size = "md",
  className,
  contentClassName,
  hideNav = false,
}: AdminWizardProps) {
  if (variant === "tabs") {
    return (
      <AdminTabs
        className={className}
        contentClassName={contentClassName}
        value={items[current]?.key}
        onValueChange={(value) => {
          const index = items.findIndex((item) => item.key === value);
          if (index !== -1) onChange(index);
        }}
        items={items.map((item) => ({
          value: item.key,
          label: item.title,
          content: item.content,
          disabled: item.disabled,
        }))}
      />
    );
  }

  const isFirst = current === 0;
  const isLast = current === items.length - 1;

  return (
    <div className={className}>
      <AdminSteps
        className="mb-6"
        size={size}
        current={current}
        onChange={onChange}
        items={items.map((item) => ({
          title: item.title,
          description: item.description,
          disabled: item.disabled,
        }))}
      />
      <div className={contentClassName}>{items[current]?.content}</div>
      {!hideNav && (
        <div className="mt-4 flex justify-between border-t pt-4">
          <Button
            type="button"
            variant="outline"
            disabled={isFirst}
            onClick={() => onChange(Math.max(current - 1, 0))}
          >
            {prevLabel}
          </Button>
          <Button
            type="button"
            disabled={isLast}
            onClick={() => onChange(Math.min(current + 1, items.length - 1))}
          >
            {nextLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
